import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSale } from '@/hooks/useSales';
import { useSalesSettings } from '@/hooks/useSalesSettings';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { printSaleInvoice } from '@/services/print/printSale';
import type { PaperWidth } from '@/services/print/printTypes';
import { formatMoney, formatDateTime } from '@/lib/format';
import {
  paymentLabel,
  saleCashPortion,
  saleCardPortion,
  invoiceStatusMeta,
} from '@/lib/types';
import DeleteInvoiceDialog from '@/pages/sales/components/DeleteInvoiceDialog';

const DELETABLE_STATUSES = ['completed', 'partial_refund'];

export default function SaleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { sale, items, loading, error, reload } = useSale(id);
  const { settings } = useSalesSettings();
  const { user, profile, isAdmin } = useAuth();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const branch = sale?.branch ?? null;
  const currencySymbol = settings?.currency_symbol || 'RD$';
  const showLogo = settings?.invoice_show_logo ?? true;
  const showAddress = settings?.invoice_show_address ?? true;
  const showPhone = settings?.invoice_show_phone ?? true;
  const showRnc = settings?.invoice_show_rnc ?? true;
  const showCashier = settings?.invoice_show_cashier ?? true;
  const showDatetime = settings?.invoice_show_datetime ?? true;
  const showPayment = settings?.invoice_show_payment_method ?? true;
  const footerMessage = settings?.invoice_footer_message || branch?.description || 'Gracias por su compra.';

  // Formato de papel y impresora configurados en Ajustes. Nunca se asume 80mm.
  const printFormat: PaperWidth = (settings?.print_format as PaperWidth) || '80mm';
  const configuredPrinter = settings?.default_printer?.trim() || '';
  const printerName =
    configuredPrinter && configuredPrinter !== 'Sin impresora configurada' ? configuredPrinter : null;
  const invoiceShowFlags = {
    showLogo,
    showCashier,
    showPayment,
    showAddress,
    showPhone,
    showRnc,
    showDatetime,
    footerMessage,
  };

  const codeDiscount = Number(sale?.discount_code_amount ?? 0);
  const lineDiscount = Math.max(0, Number(sale?.discount ?? 0) - codeDiscount);
  const hasWholesale = items.some((i) => i.is_wholesale);

  // Impresión automática al entrar con ?print (por ejemplo, tras cobrar).
  useEffect(() => {
    if (sale && items.length > 0 && (location.state as { print?: boolean })?.print) {
      const t = setTimeout(() => {
        printSaleInvoice({
          branch,
          sale,
          items,
          currencySymbol,
          paperWidth: printFormat,
          copies: 1,
          printerName,
          ...invoiceShowFlags,
        });
      }, 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale, items, location.state]);

  const handleReprint = async () => {
    if (!sale) return;
    const result = await printSaleInvoice({
      branch,
      sale,
      items,
      currencySymbol,
      paperWidth: printFormat,
      copies: 1,
      printerName,
      ...invoiceShowFlags,
    });

    // Registro de auditoría de reimpresión.
    const userName =
      [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
      user?.email ||
      'Desconocido';

    await supabase.from('audit_log').insert({
      user_id: user?.id ?? null,
      user_name: userName,
      branch_id: sale.branch_id ?? null,
      action: 'reimpresión',
      target: 'factura',
      details: {
        invoice_number: sale.invoice_number,
        sale_id: sale.id,
        success: result.success,
      },
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Link
          to="/sales"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> Volver a ventas
        </Link>
        {sale && (
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                invoiceStatusMeta(sale.status).badge
              }`}
            >
              {invoiceStatusMeta(sale.status).label}
            </span>
            <button
              onClick={handleReprint}
              className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              <i className="ri-printer-line" /> Reimprimir
            </button>
            {isAdmin && DELETABLE_STATUSES.includes(sale.status) && (
              <button
                onClick={() => setDeleteOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-accent-300 px-3 py-2 text-sm font-medium text-accent-700 hover:bg-accent-50 whitespace-nowrap transition cursor-pointer"
              >
                <i className="ri-delete-bin-6-line" /> Eliminar
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : error ? (
        <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      ) : sale ? (
        <div className="mt-6 max-w-2xl mx-auto">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            {/* Encabezado */}
            <div className="text-center">
              {showLogo && branch?.logo_url && (
                <img src={branch.logo_url} alt="Logo" className="w-16 h-16 mx-auto object-contain mb-3" />
              )}
              <h1 className="font-heading text-xl font-bold text-foreground-950">
                {branch?.name || 'Factura'}
              </h1>
              {branch?.legal_name && (
                <p className="text-sm text-foreground-600">{branch.legal_name}</p>
              )}
              {showAddress && branch?.address && (
                <p className="mt-1 text-sm text-foreground-600">{branch.address}</p>
              )}
              {showPhone && branch?.phone && (
                <p className="text-sm text-foreground-600">{branch.phone}</p>
              )}
              {showRnc && branch?.rnc && (
                <p className="text-sm text-foreground-600">RNC: {branch.rnc}</p>
              )}
            </div>

            {/* Datos de factura */}
            <div className="mt-6 flex flex-wrap justify-between gap-4 text-sm border-t border-b border-background-200 py-4">
              <div>
                <p className="text-foreground-600">Factura</p>
                <p className="font-semibold text-foreground-950">{sale.invoice_number}</p>
              </div>
              {showDatetime && (
                <div>
                  <p className="text-foreground-600">Fecha y hora</p>
                  <p className="font-medium text-foreground-900">{formatDateTime(sale.created_at)}</p>
                </div>
              )}
              {showCashier && (
                <div>
                  <p className="text-foreground-600">Cajero</p>
                  <p className="font-medium text-foreground-900">{sale.cashier_name || '—'}</p>
                </div>
              )}
              <div>
                <p className="text-foreground-600">Cliente</p>
                <p className="font-medium text-foreground-900">{sale.customer_name || 'Cliente general'}</p>
              </div>
            </div>

            {/* Items */}
            <div className="mt-4">
              <table className="w-full text-sm">
                <thead className="text-foreground-600 text-left border-b border-background-200">
                  <tr>
                    <th className="py-2 font-medium">Producto</th>
                    <th className="py-2 font-medium text-center">Cant.</th>
                    <th className="py-2 font-medium text-right">Precio</th>
                    <th className="py-2 font-medium text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="py-2.5">
                        <div className="text-foreground-900">{item.product_name}</div>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          {item.is_wholesale && (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-500 text-background-50">
                              MAYORISTA
                            </span>
                          )}
                          {(item.discount_type === 'manual_percent' ||
                            item.discount_type === 'manual_fixed') && (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary-500 text-background-50">
                              DESCUENTO
                            </span>
                          )}
                          {(item.discount_type === 'product' || item.discount_type === 'offer') && (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-secondary-500 text-background-50">
                              DESC. -{item.discount_percentage}%
                            </span>
                          )}
                        </div>
                        {Number(item.discount) > 0 && (
                          <div className="mt-0.5 text-xs text-accent-700">
                            Descuento
                            {item.discount_type === 'manual_fixed'
                              ? ''
                              : item.discount_percentage != null
                                ? ` ${item.discount_percentage}%`
                                : ''}
                            : -{formatMoney(Number(item.discount), currencySymbol)}
                          </div>
                        )}
                        {item.discount_authorized_by_name && (
                          <div className="text-[10px] text-foreground-400">
                            Autorizado por {item.discount_authorized_by_name}
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 text-center text-foreground-700">{item.quantity}</td>
                      <td className="py-2.5 text-right text-foreground-700">
                        {formatMoney(Number(item.unit_price), currencySymbol)}
                      </td>
                      <td className="py-2.5 text-right font-medium text-foreground-950">
                        {formatMoney(Number(item.subtotal), currencySymbol)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totales */}
            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-foreground-600">Subtotal</span>
                <span className="font-medium text-foreground-900">{formatMoney(Number(sale.subtotal), currencySymbol)}</span>
              </div>
              {hasWholesale && (
                <div className="flex justify-between">
                  <span className="text-foreground-600">Precio mayorista aplicado</span>
                  <span className="font-medium text-accent-700">✓</span>
                </div>
              )}
              {lineDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-foreground-600">Descuentos de artículos</span>
                  <span className="font-medium text-accent-700">-{formatMoney(lineDiscount, currencySymbol)}</span>
                </div>
              )}
              {codeDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-foreground-600">
                    Descuento código {sale.discount_code ? `(${sale.discount_code})` : ''}
                  </span>
                  <span className="font-medium text-accent-700">-{formatMoney(codeDiscount, currencySymbol)}</span>
                </div>
              )}
              {Number(sale.tax) > 0 && (
                <div className="flex justify-between">
                  <span className="text-foreground-600">Impuesto</span>
                  <span className="font-medium text-foreground-900">{formatMoney(Number(sale.tax), currencySymbol)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-background-200 pt-2">
                <span className="font-heading font-semibold text-foreground-950">Total</span>
                <span className="font-heading text-lg font-bold text-foreground-950">
                  {formatMoney(Number(sale.total), currencySymbol)}
                </span>
              </div>
              {showPayment && (
                <div className="flex justify-between">
                  <span className="text-foreground-600">Método de pago</span>
                  <span className="font-medium text-foreground-900">
                    {paymentLabel(sale.payment_method)}
                  </span>
                </div>
              )}
              {sale.payment_method === 'cash' && Number(sale.amount_received) > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-foreground-600">Recibido</span>
                    <span className="font-medium text-foreground-900">
                      {formatMoney(Number(sale.amount_received), currencySymbol)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-600">Cambio</span>
                    <span className="font-medium text-foreground-900">
                      {formatMoney(Number(sale.change_amount), currencySymbol)}
                    </span>
                  </div>
                </>
              )}
              {sale.payment_method === 'mixed' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-foreground-600">Efectivo</span>
                    <span className="font-medium text-foreground-900">
                      {formatMoney(saleCashPortion(sale), currencySymbol)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-600">Tarjeta</span>
                    <span className="font-medium text-foreground-900">
                      {formatMoney(saleCardPortion(sale), currencySymbol)}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Mensaje final */}
            <p className="mt-6 text-center text-sm text-foreground-600 italic">{footerMessage}</p>
          </div>
        </div>
      ) : (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 p-10 text-center text-foreground-600">
          Factura no encontrada.
        </div>
      )}

      {deleteOpen && sale && (
        <DeleteInvoiceDialog
          saleId={sale.id}
          invoiceNumber={sale.invoice_number}
          onClose={() => setDeleteOpen(false)}
          onSuccess={() => {
            setDeleteOpen(false);
            reload();
            setToast('Factura eliminada correctamente.');
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 rounded-md bg-foreground-950 text-background-50 px-4 py-2.5 text-sm font-medium">
          <i className="ri-checkbox-circle-line text-accent-300" /> {toast}
        </div>
      )}
    </div>
  );
}